import { Component, OnInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { PageBase, RentalInfo, RentalStatus } from 't4core';
import { AppIntegrationService, AppSettingsService, FindLockResult } from 't4-app-integration';
import { NavigationResolver, NavigationService, ActiveRentalService, LayoutService } from 'app-components';
import { Subscription } from 'rxjs';
import { ConfirmObjectDialog } from '../../dialogs/confirm-object/confirm-object';

@Component({
    selector: 'app-nfc-nolock',
    templateUrl: './nfc-nolock.component.html',
    styleUrls: ['./nfc-nolock.component.css'],
    standalone: false
})
export class NFCNoLockComponent extends PageBase implements OnInit, OnDestroy {

  public count = 0;
  public unlocked = false;

  public booking: RentalInfo;
  public status = RentalStatus;
  public firstLoad: boolean = true;
  public objectId: number;
  public unlockingInfo: string;
  public alternativesLoaded: boolean = false;
  public isAndroid: boolean = false;
  public isBauhaus: boolean = false;

  public event: string = ""; // Pickup/Return/Active etc.


  public device: string = null;

  // States
  public isWaiting: boolean = true;
  public isSearching: boolean = false;
  public hasFailed: boolean = false;
  public hasCompleted: boolean = false;

  public failReason: number;

  private timerSubscription: Subscription;
  private nfcSubscription: Subscription;
  private nfcSearchStopSubscription: Subscription;

  private completeCount: number = 0;

  constructor(el: ElementRef, public appSvc: AppIntegrationService, private aviationService: NavigationService, private rentalService: ActiveRentalService, public layoutService: LayoutService, public resolver: NavigationResolver, public appSettings: AppSettingsService, private navigator: NavigationService) {
    super(el);

    var gotoStep = this.navigator.getParam("step");
    if (gotoStep == "IsComplete") {
      this.isWaiting = false;
      this.isSearching = false;
      this.hasCompleted = true;
    }

    this.appSvc.requestDeviceState();

    this.device = this.getMobileOperatingSystem();

    this.timerSubscription = this.appSvc.getSecondTimer().subscribe(x => {
      // Update search timer
      if (this.isSearching) {
        if (this.count < 20) {
          this.count++;
        } else {
          this.Insights.logEvent("No tag found; Timed out; Trailer: " + this.booking.ObjectId);
          this.fail(2);
        }
      }

      // Redirect when completed for 10 seconds
      if (this.hasCompleted) {
        if (this.completeCount > 10) this.next();
        else this.completeCount++;
      }
    });
  }

  async ngOnInit() {
    // If the current object does not have a tag, continue immediately.
    if (!await this.rentalService.hasTag()) {
      this.ngOnDestroy();

      this.rentalService.fetchSecurity(false);
      this.aviationService.executeCommand("NoTag");
      setTimeout(() => { this.aviationService.executeCommand("Next"); }, 100);
      
      return;
    }

    this.Insights.logEvent("Has tag");
    this.booking = await this.rentalService.getBooking();

    //Bypass temporarily for users in Byggmax Norway for open trailers NFC tag issues.
    const allowedObjectIds = [362226, 362227, 362229, 362293, 362294, 362298, 362299, 362303, 362318, 362319, 362321, 362328, 362542, 362544, 362545, 362546, 362593, 362595, 362598, 365203, 365204];
    if (allowedObjectIds.includes(this.booking.ObjectId)) {
      this.Api.post<boolean>("/Rental/LogEvent", "Bypassing nfc temporarily for Byggmax Norway open trailers till issue is fixed.", { rentalId: this.booking.Id });
      this.Insights.trackTrace("Bypassing nfc temporarily for Byggmax Norway open trailers till issue is fixed.");
      setTimeout(() => { this.aviationService.executeCommand("Next"); }, 100);
    }

    var state = "On";
    if (this.appSvc.Device.Nfc == 0) state = "Not available";
    if (this.appSvc.Device.Nfc == 2) state = "Off";

    this.Insights.trackTrace("Begin Nfc unlock! Nfc state: " + state);
    this.Api.post<boolean>("/Rental/LogEvent", "Begin Nfc unlock! Nfc state: " + state, { rentalId: this.booking.Id });

    this.objectId = this.booking.ObjectId;

    // Register listeners
    this.rentalService.startActiveNfcScanning();
    this.nfcSubscription = this.appSvc.nfcDetected.subscribe(tag => {
      var devices = this.booking.RentalObject.Locks;
      var info = "Found Nfc tag: " + tag;


      var parts = tag.split('://');
      if (parts.length != 2) {
        this.Insights.trackTrace("Invalid tag: " + tag);
        this.fail(1);
      }
      var app = parts[0];

      parts = parts[1].split('#');
      var regno = parts[0].replace("*", "");
      var action = parts.length > 1 ? parts[1] : '';

      var expectedTag = devices.find(x => x.DeviceType == 2);

      // Handle result
      if (tag && devices.some(x => x.Id.toUpperCase() == regno.toUpperCase())) {
        this.Insights.logEvent("NFC tag scanned");
        this.Api.post<boolean>("/Rental/LogEvent", info, { rentalId: this.booking.Id });

        // Continue process (Twice if we officially havent started looking yet)
        if (this.isWaiting) this.next();
        this.next();


      } else if (tag) {
        info += "; Wrong tag";
        if (expectedTag) info += "; Expected: " + expectedTag.Id;

        this.Insights.trackTrace(info);
        this.Api.post<boolean>("/Rental/LogEvent", info, { rentalId: this.booking.Id });
        this.fail(1);
      } else {
        info = "No tag found";
        this.Insights.trackTrace(info);
        this.Api.post<boolean>("/Rental/LogEvent", info, { rentalId: this.booking.Id });
        this.fail(2);
      }
    });
    this.nfcSearchStopSubscription = this.appSvc.nfcSearchStopped.subscribe(n => {
      if (this.hasCompleted) return;
      var info = "Stopped Active NFC Scanning";
      this.Api.post<boolean>("/Rental/LogEvent", info, { rentalId: this.booking.Id });
      this.rentalService.stopActiveNfcScanning();
      this.fail(3);
    });
  }

  ngOnDestroy() {
    this.rentalService.stopActiveNfcScanning();
      
    if (this.nfcSubscription) this.nfcSubscription.unsubscribe();
    if (this.timerSubscription) this.timerSubscription.unsubscribe();
    if (this.nfcSearchStopSubscription) this.nfcSearchStopSubscription.unsubscribe();
  }

  public next() {
    this.count = 0;
    if (this.isWaiting) {
      this.isWaiting = false;
      this.isSearching = true;
    } else if (this.isSearching) {
      this.isSearching = false;
      this.hasCompleted = true;
    } else if (this.hasCompleted) {
      this.ngOnDestroy();

      this.rentalService.fetchSecurity(false);
      this.aviationService.executeCommand("Next");
    }
  }

  private fail(reason?: number) {
    if (this.hasFailed) return;
    if (this.isWaiting) this.next();
    this.hasFailed = true;
    this.isSearching = false;
    this.failReason = reason;
  }

  private retryCount: number = 0;
  public retry() {
    this.retryCount++;

    this.count = 0;
    if (this.hasFailed) {
      this.hasFailed = false;
      this.failReason = null;
      this.isWaiting = true;
      this.beginSearch();
    }
  }

  public skip() {
    const dialogRef = this.Dialog.open(ConfirmObjectDialog, {
      disableClose: true,
      width: '350px',
      position: { top: '50px' },
      data: { object: this.booking.RentalObject }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.ngOnDestroy();

        this.rentalService.fetchSecurity(false);
        this.aviationService.executeCommand("Next");
      } else {
      }
    })
    
  }



  public beginSearch() {
    this.next();
    this.count = 0;

    this.Insights.trackTrace("Looking for Nfc tag");

    // Ask the device to start searching
    this.appSvc.startNfcSearch();

    // Bypass if demo settings or if nfc not available
    if (
      (localStorage.getItem('bypassLock') && localStorage.getItem('bypassLock') == "true") ||
      this.appSvc.Device.Nfc == 0
    ) {
      setTimeout(() => {
        this.Insights.trackTrace("Bypassing nfc");
        if (this.isWaiting) this.next();
        this.next();
        this.ngOnDestroy();
      }, 7000);
    }
    
  }

  public getMobileOperatingSystem() : string {
    var userAgent = navigator.userAgent || navigator.vendor;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return "Windows Phone";
  }

  if (/android/i.test(userAgent)) {
    return "Android";
  }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    var win: any = window;
    if (/iPad|iPhone|iPod/.test(userAgent) && !win.MSStream) {
      return "iOS";
  }

  return "unknown";
}
}
